import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight,
  Activity,
  PlusCircle,
  Users as UsersIcon,
  Bell
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../utils/api";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState({
    total_leads: 0,
    new_leads_today: 0,
    leads_pending_followup: 0,
    converted_leads: 0,
    lost_leads: 0,
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const renderText = (text) => {
    if (!text) return "";
    if (typeof text === "string") return text;
    return text.en || text.ar || Object.values(text)[0] || "";
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const { data } = await api.get("/admin/dashboard/metrics");
      setMetrics(data.metrics);
      setRecentLeads(data.recent_leads);
    } catch (error) {
      toast.error("Failed to load dashboard metrics");
    } finally {
      setIsLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Leads",
      value: metrics.total_leads,
      icon: Users,
      color: "bg-blue-500",
    },
    {
      title: "New Today",
      value: metrics.new_leads_today,
      icon: TrendingUp,
      color: "bg-green-500",
    },
    {
      title: "Pending Follow-up",
      value: metrics.leads_pending_followup,
      icon: Clock,
      color: "bg-amber-500",
    },
    {
      title: "Converted",
      value: metrics.converted_leads,
      icon: CheckCircle,
      color: "bg-emerald-600",
    },
    {
      title: "Lost",
      value: metrics.lost_leads,
      icon: XCircle,
      color: "bg-red-500",
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <Link
          to="/admin/leads"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition"
        >
          Add New Lead
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">
                  {stat.title}
                </p>
                <h3 className="text-3xl font-bold text-gray-900">
                  {stat.value}
                </h3>
              </div>
              <div className={`${stat.color} p-3 rounded-lg text-white`}>
                <stat.icon size={20} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <Link 
          to="/admin/leads?action=add" 
          className="group bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition-all duration-300 hover:-translate-y-1"
        >
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
            <PlusCircle size={24} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Add New Lead</h3>
            <p className="text-sm text-gray-500">Capture a new referral</p>
          </div>
        </Link>
        <Link 
          to="/admin/leads" 
          className="group bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition-all duration-300 hover:-translate-y-1"
        >
          <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600 group-hover:bg-purple-600 group-hover:text-white transition-colors">
            <UsersIcon size={24} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">View Recent Leads</h3>
            <p className="text-sm text-gray-500">Manage your active directory</p>
          </div>
        </Link>
        <Link 
          to="/admin/tasks" 
          className="group bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition-all duration-300 hover:-translate-y-1"
        >
          <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center text-amber-600 group-hover:bg-amber-600 group-hover:text-white transition-colors">
            <Bell size={24} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Follow-up Reminders</h3>
            <p className="text-sm text-gray-500">View your pending tasks</p>
          </div>
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Leads
            </h2>
          </div>
          <Link
            to="/admin/leads"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
          >
            View All <ChevronRight size={16} />
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-sm border-b border-gray-100">
                <th className="px-6 py-3 font-medium">Name</th>
                <th className="px-6 py-3 font-medium">Service</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Assigned To</th>
                <th className="px-6 py-3 font-medium">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {recentLeads.length > 0 ? (
                recentLeads.map((lead) => (
                  <tr
                    key={lead.id}
                    className="hover:bg-gray-50/50 transition duration-150"
                  >
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">
                        {renderText(lead.name)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {lead.phone || lead.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {renderText(lead.service_type)?.replace("_", " ") || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="capitalize text-sm font-medium text-gray-700">
                        {lead.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {lead.assigned_user
                        ? lead.assigned_user.name
                        : "Unassigned"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No recent leads found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
